import axios, { CancelToken } from 'axios';

import config from "@app/utils/config";
import HTTPResponse from "../utils/http/interfaces/HttpResponse";
import httpVerbs from "../utils/http/httpVerbs";
import http from "../utils/http";
import { ValueType } from "@app/types/ValueType";
import { getParameterizedUrl, getParameterizedUrlWith } from "@app/utils";
import { ICheckoutFile } from "@app/features/FileArea/CheckoutOption";
import { EmailFilesPayload } from "@app/types/EmailFilesPayload";
import { ServiceType } from '@app/types/ServiceType';
import { mapApiErrorToTelemetry as mapApiErrorToTelemetryBase } from '@app/utils/telemetry';
import { CreateSingleDocumentRequest } from '@app/types/SingleDocumentCreation';

export const upload = (
  fileName: string,
  file: File,
  siteId: string,
  referenceNo: string | null,
  chunkId: number,
  totalChunkCount: number,
  onUploadProgress?: (progressEvent: any) => void,
  cancelToken?: CancelToken
): Promise<HTTPResponse<any>> => {
  const formData = new FormData();
  formData.append('file', file, fileName);
  formData.append('siteid', siteId);
  referenceNo && formData.append('ReferenceNumber', referenceNo);
  formData.append('chunkId', chunkId.toString());
  formData.append('totalChunkCount', totalChunkCount.toString());

  return http(
    {
      method: httpVerbs.POST,
      url: config.api[ServiceType.FileStorage].uploadFiles,
      headers: {
        'content-type': 'multipart/form-data',
      },
      data: formData,
      onUploadProgress,
      cancelToken,
    },
    ServiceType.FileManagement.toString()
  );
};

export const mapApiErrorToTelemetry = (
  operation: string,
  error: unknown
): Record<string, unknown> => {
  return mapApiErrorToTelemetryBase(operation, error);
};

export const getFolderStructureBySite = (siteId: string): Promise<HTTPResponse<any>> => {
  return http(
    {
      method: httpVerbs.GET,
      url: getParameterizedUrl(config.api[ServiceType.FileManagement].folderStructureBySite, siteId),
    },
    ServiceType.FileManagement.toString()
  );
};

export const getFileAreaIdBySite = (siteId: string): Promise<HTTPResponse<any>> => {
  return http(
    {
      method: httpVerbs.GET,
      url: getParameterizedUrl(config.api[ServiceType.FileManagement].fileAreaIdBySite, siteId),
    },
    ServiceType.FileManagement.toString()
  );
};

export const getFileStatuses = (): Promise<HTTPResponse<[ValueType]>> => {
  return http(
    {
      method: httpVerbs.GET,
      url: config.api[ServiceType.FileManagement].fileStatuses,
    },
    ServiceType.FileManagement.toString()
  );
};

export const getYears = (): Promise<HTTPResponse<[number]>> => {
  return http({
    method: httpVerbs.GET,
    url: config.api[ServiceType.FileManagement].years,
  });
};

export const getProjectsBySite = (siteId: string): Promise<HTTPResponse<[ValueType]>> => {
  return http({
    method: httpVerbs.GET,
    url: getParameterizedUrl(config.api[ServiceType.FileManagement].projectsBySite, siteId),
  });
};

export const getUsers = (siteId: string): Promise<HTTPResponse<[ValueType]>> => {
  return http({
    method: httpVerbs.GET,
    url: getParameterizedUrl(config.api[ServiceType.FileManagement].usersBySite, siteId),
  });
};

export const getFileTypes = () => {
  return Promise.resolve({
    data: ['PDF', 'JPEG', 'PNG', 'DOCX', 'QBW', 'QBX', 'QBY', 'XLSX'],
    status: 200,
  });
};

export const saveFileDetails = (data: any) => {
  return http({
    method: httpVerbs.POST,
    url: config.api[ServiceType.FileManagement].saveFileDetails,
    data: data,
  });
};

export const createSingleDocument = (data: CreateSingleDocumentRequest): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.POST,
    url: config.api[ServiceType.FileStorage].createSingleDocument,
    data,
  });
};

export const saveAcceptFileDetails = (data: any) => {
  return http({
    method: httpVerbs.PATCH,
    url: config.api[ServiceType.Portal].portalFileActionsAccept,
    data: data,
  });
};

export const removeFiles = (siteId: string, referenceNumbers: any[], removeChunks = false) => {
  if (referenceNumbers.length > 0) {
    return http({
      method: httpVerbs.DELETE,
      url: config.api[ServiceType.FileStorage].uploadFiles,
      data: {
        siteId,
        referenceNumbers,
      },
      params: removeChunks ? { removeChunks } : null,
    });
  }
};

export const removeFilesWithToken = (siteId: string, referenceNumbers: any[], removeChunks = false) => {
  if (referenceNumbers.length > 0) {
    return http(
      {
        method: httpVerbs.DELETE,
        url: config.api[ServiceType.FileStorage].uploadFiles,
        data: {
          siteId,
          referenceNumbers,
        },
        params: removeChunks ? { removeChunks } : null,
      },
      {
        authorization: 'portal-upload-token',
      }
    );
  }
};

export const getFileDetailsByFileId = (
  fileId: string,
  clientId: string
): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.GET,
    url: getParameterizedUrl(
      config.api[ServiceType.FileManagement]
        .fileDetailsByFile,
      fileId
    ),
    params: { ClientId: clientId },
  });
};

export const updateFileDetailsByFileId = (fileId: string, data: any): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.PATCH,
    url: getParameterizedUrl(config.api[ServiceType.FileManagement].fileDetailsByFile, fileId),
    data,
  });
};

export const checkoutFile = (
  data: ICheckoutFile,
  clientId?: string
): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.POST,
    url: getParameterizedUrl(config.api[ServiceType.FileManagement].checkout, data.id),
    data: {
      note: data.checkNote,
      returnDate: data.returnDate,
      clientId: clientId
    },
  });
};

export const undoCheckoutFile = (
  fileId: string,
  clientId: string
): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.POST,
    url: getParameterizedUrl(config.api[ServiceType.FileManagement].undoCheckout, fileId),
    data: {},
    params: { clientId: clientId },
  });
};

export const deleteFile = (fileIds: any, clientId: string): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.DELETE,
    url:
      config.api[ServiceType.FileManagement]
        .deleteFilesByIds,
    data: { fileIds: fileIds, clientId: clientId },
  });
};

export const updateFileStatus = (
  fileIds: any,
  statusId: number,
  clientId?: string
): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.PATCH,
    url: config.api[ServiceType.FileManagement].fileStatus,
    data: { fileIds: fileIds, statusId: statusId, clientId: clientId },
  });
};

export const updateAssigneeNStatus = (fields: {
  fileIds: string[];
  statusId: number;
  assigneeId: number;
  note?: string;
  clientId?: string;
}): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.PUT,
    url: config.api[ServiceType.FileManagement].updateAssigneeNStatus,
    data: fields,
  });
};

export const downloadFile = (fileId: string, clientId?: string, isPreview?: boolean): Promise<HTTPResponse<any>> => {
  const params: any = {};
  
  if (clientId !== undefined) {
    params.clientId = clientId;
  }
  
  if (isPreview !== undefined) {
    params.isPreview = isPreview;
  }

  return http({
    method: httpVerbs.GET,
    url: getParameterizedUrl(
      config.api[ServiceType.FileManagement].downloadFile,
      fileId
    ),
    params,
  }).then((response) => {
    return http({
      method: httpVerbs.GET,
      url: ServiceType.FileStorage.toString() + response.data.uri,
      responseType: 'arraybuffer',
    });
  });
};

export const downloadZipFile = (fileIds: any, clientId?: string): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.POST,
    url:
      config.api[ServiceType.FileManagement].downloadPublishedZipFile,
    data: { fileIds: fileIds, clientId: clientId },
  }).then((response) => {
    return http({
      method: httpVerbs.POST,
      url: ServiceType.FileStorage.toString() + response.data.uri,
      data: { files: response.data.payload },
      responseType: 'arraybuffer',
    });
  });
};

export const downloadPortalFile = (fileId: string): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.GET,
    url: getParameterizedUrl(config.api[ServiceType.Portal].downloadPortalFiles, fileId),
  }).then((response) => {
    return http({
      method: httpVerbs.GET,
      url: ServiceType.FileStorage.toString() + response.data.uri,
      responseType: 'arraybuffer',
    });
  });
};

export const downloadPortalZipFile = (fileIds: any, clientId: string): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.POST,
    url: config.api[ServiceType.Portal].downloadPortalZipFiles,
    data: { fileIds: fileIds },
  }).then((response) => {
    return http({
      method: httpVerbs.POST,
      url: ServiceType.FileStorage.toString() + response.data.uri,
      data: { files: response.data.payload },
      responseType: 'arraybuffer',
    });
  });
};

export const getCheckoutFileListBySiteId = (binderId: any) => {
  return http({
    method: httpVerbs.GET,
    url: config.api[ServiceType.FileManagement].checkedOutFiles,
    params: { binderId: binderId },
  });
};

export const getCheckoutFilesBySiteIdAndFileId = (binderId: any, fileId: any) => {
  return http({
    method: httpVerbs.GET,
    url: config.api[ServiceType.FileManagement].checkedOutFiles,
    params: { binderId: binderId, fileid: fileId },
  });
};

export const canCheckinByFileId = (fileId: any, clientId: string) => {
  return http({
    method: httpVerbs.HEAD,
    url: getParameterizedUrl(config.api[ServiceType.FileManagement].canCheckinByFileId, fileId),
    params: { clientId: clientId },
  });
};

export const checkCheckoutFileListBySiteId = (binderId: any) => {
  return http({
    method: httpVerbs.HEAD,
    url: config.api[ServiceType.FileManagement].checkedOutFiles,
    params: { binderId: binderId },
  });
};

export const checkInFiles = (binderId: any, fileList: any, clientId?: string) => {
  const formattedFileList = fileList?.map((file: any) => ({
    fileId: file.id,
    referenceNumber: file.referenceNumber,
  }));
  return http({
    method: httpVerbs.POST,
    url: config.api[ServiceType.FileManagement].checkiIn,
    data: { binderId: binderId, files: formattedFileList, clientId: clientId },
  });
};

export const recategorizeFiles = (
  fileIds: any,
  destinationFolderId: number,
  binderId: string,
  mode?: number,
  years?: number,
  clientId?: string
): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.PATCH,
    url:
      config.api[ServiceType.FileManagement].recategorize,
    data: { fileIds: fileIds, destinationBinderNodeId: destinationFolderId, binderId: binderId, clientId: clientId, retention: {mode : mode, years: years} },
  });
};

export const moveFilesService = (
  fileIds: any,
  destinationFolderId: number,
  sourceBinderId: string,
  destinationBinderId: string,
  mode: number,
  years: number,
  clientId?: string,

): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.POST,
    url:
      config.api[ServiceType.FileManagement].moveFiles,
    data: { fileIds: fileIds, destinationBinderNodeId: destinationFolderId, sourceBinderId: sourceBinderId, destinationBinderId: destinationBinderId, clientId: clientId, retention: {mode : mode, years: years} },
  });
};

export const getCheckoutFileHistoryListByFileId = (
  fileId: any,
  clientId: string
): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.GET,
    url: getParameterizedUrl(
      config.api[ServiceType.FileManagement]
        .checkedOutFilesHistory,
      fileId
    ),
    params: { ClientId: clientId }
  });
};

export const getAssignmentFileHistoryListByFileId = (
  fileId: any,
  clientId: string
): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.GET,
    url: getParameterizedUrl(
      config.api[ServiceType.FileManagement]
        .assignmentFilesHistory,
      fileId
    ),
    params: { ClientId: clientId }
  });
};

export const getPendingFilesCardDetails = (channelId: any) => {
  return http({
    method: httpVerbs.GET,
    url: config.api[ServiceType.Portal].getPendingFilesCard,
    params: { channelId },
  });
};

export const getFullfiledFilesDetails = (channelId: any) => {
  return http({
    method: httpVerbs.GET,
    url: config.api[ServiceType.Portal].getFullfilledFiles,
    params: { channelId },
  });
};

export const checkSiteAvailable = (channelId: any) => {
  return http({
    method: httpVerbs.HEAD,
    url: getParameterizedUrlWith(config.api[ServiceType.MasterData].checkSiteByChannel, [{ name: 'channelId', value: channelId }]),
  });
};

export const publishFilesRequest = (
  fileIds: string[],
  siteId: string,
  expirationDate: Date | undefined,
  clientId: string,
  publishedBinderNodeId: number,
): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.POST,
    url: config.api[ServiceType.FileManagement].publishFiles,
    data: { fileIds, siteId, expirationDate: expirationDate?.toISOString(), clientId: clientId, publishedBinderNodeId },
  });
};

export const unpublishFilesRequest = (
  fileIds: string[],
  siteId: string,
  clientId: string
): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.DELETE,
    url: config.api[ServiceType.FileManagement].publishFiles,
    data: { fileIds, siteId, clientId },
  });
};

export const copyFiles = (
  fileUploadReferences: any,
  clientId?: string
): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.POST,
    url:
      config.api[ServiceType.FileStorage].copyFiles,
    data: { referenceNumbers: fileUploadReferences, clientId: clientId },
  });
};

export const reNameFiles = (
  renamedFile: any,
  clientId?: string
): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.PATCH,
    url:
      config.api[ServiceType.FileManagement].reNameFiles,
    data: { files: renamedFile, clientId: clientId },
  });
};


export const pinUnPinFiles = (
  fileIds: any,
  pinned: boolean,
  clientId: string
): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.PATCH,
    url:
      config.api[ServiceType.FileManagement].pin,
    data: { fileIds, pinned, clientId },
  });
};

export const linkToSite = (links: any): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.POST,
    url: config.api[ServiceType.FileManagement].linked,
    data: links,
  });
};

export interface LinkedBinders {
  id: string;
  name: string;
  displayText: string;
}

export interface FileLinksResponse {
  fileId: string;
  title: string;
  fileName: string;
  binders: LinkedBinders[];
}

export const getLinksWithBinders = (fileIds: string[]): Promise<HTTPResponse<FileLinksResponse[]>> => {
  return http({
    method: httpVerbs.POST,
    url: config.api[ServiceType.FileManagement].links,
    data: { fileIds },
  });
};

export interface UnlinkFileLinksRequest {
  fileId: string;
  binderIds: string[];
}

export const unlinkFilesFromBinders = (links: UnlinkFileLinksRequest[]): Promise<HTTPResponse<boolean>> => {
  return http({
    method: httpVerbs.POST,
    url: config.api[ServiceType.FileManagement].unlinked,
    data: links,
  });
};

export const getJobs = (clientCode: any, jobTypeId: any) => {
  return http({
    method: httpVerbs.GET,
    url: config.api[ServiceType.MasterData].getJobs,
    params: { clientCode: clientCode, jobTypeId: jobTypeId },
  });
};

export interface UpdateFileAreaRequest {
  binderName: string;
  binderStatus: number;
  binderYear: number;
  binderId: string;
}

export const updateFileAreaByBinderId = (data: UpdateFileAreaRequest): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.PUT,
    url: getParameterizedUrl(config.api[ServiceType.FileManagement].updateFileArea, data.binderId),
    data,
  });
};

export const getBinderFileAreaNodes = (binderId: string): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.GET,
    url: getParameterizedUrl(config.api[ServiceType.FileManagement].binderFileAreaNodes, binderId),
  });
};

export const saveFileArea = (binderName: string, siteId: string, templateId: string, jobIds: number, binderStatus: number, binderYear: number) => {
  return http({
    method: httpVerbs.POST,
    url: config.api[ServiceType.FileManagement].createFileArea,
    data: { binderName: binderName, siteId: siteId, templateId: templateId, jobIds: jobIds, binderStatus: binderStatus, binderYear: binderYear },
  });
};

export const checkFileAreaAvailabilityForGivenSite = (siteId: string) => {
  return http({
    method: httpVerbs.HEAD,
    url: getParameterizedUrlWith(config.api[ServiceType.FileManagement].fileAreaAvailabilityBySite, [
      {
        name: 'siteId',
        value: siteId,
      },
    ]),
  });
};

export const getClientAreaBinders = (siteId: string): Promise<HTTPResponse<any>> => {
  const requestUrl = `${config.api[ServiceType.FileManagement].createFileArea}?siteId=${siteId}`;
  return http({
    method: httpVerbs.GET,
    url: requestUrl,
  });
};

export interface TemplateReusedRequest {
  templateId: string;
  /** Omitted by the Auto Filing Rule Setup screen, which has no client context and asks tenant-wide. */
  siteId?: string;
}

/** The endpoint answers with a bare `true` / `false`. */
export type TemplateReusedResponse = boolean;

export const getTemplateReused = ({ templateId, siteId }: TemplateReusedRequest): Promise<HTTPResponse<TemplateReusedResponse>> => {
  return http({
    method: httpVerbs.GET,
    url: config.api[ServiceType.FileManagement].templateReused,
    params: siteId ? { siteId, templateId } : { templateId },
  });
};

/**
 * Anything that is not a bare boolean returns undefined, which callers treat as unknown and
 * fail safe. The guard is what keeps a `null`, an error payload, or an HTML error page from
 * being read as "not reused" and quietly making the Year optional.
 */
export const isTemplateReused = (data: unknown): boolean | undefined => (typeof data === 'boolean' ? data : undefined);

export const getIsMultipleBindersAllowed = () => {
  return http({
    method: httpVerbs.GET,
    url: config.api[ServiceType.MasterData].isMultipleBindersAllowed,
  });
};

export const emailFilesService = (
  payload: EmailFilesPayload

): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.POST,
    url:
      config.api[ServiceType.Bff].emailNotification,
    data: payload,
  });
};


export const verifySecureLink = (
  accessToken: string,
  secureLinkReference: string,
): Promise<HTTPResponse<any>> => {
  const API_URL = `${config.otpFlowBaseURL}/api/SecureLinks/${secureLinkReference}/verify?access_token=${accessToken}`;
  return axios.get(API_URL);
};

export const requestOtp = (
  accessToken: string,
  secureLinkReference: string,
  email: string
): Promise<HTTPResponse<any>> => {
  const API_URL = `${config.otpFlowBaseURL}/api/SecureLinks/${secureLinkReference}/request-otp?access_token=${accessToken}`;
  return axios.post(API_URL, { email });
};

export const verifyOtp = (
  accessToken: string,
  secureLinkReference: string,
  email: string,
  otp: string
): Promise<HTTPResponse<any>> => {
  const API_URL = `${config.otpFlowBaseURL}/api/SecureLinks/${secureLinkReference}/files?access_token=${accessToken}`;
  return axios.post(API_URL, { email, otp });
};

export const getReplaceTerms = () => {
  return http({
    method: httpVerbs.GET,
    url: config.api[ServiceType.MasterData].replaceTerms,
  });
};

export const getEmailConfig = () => {
  return http({
    method: httpVerbs.GET,
    url: config.api[ServiceType.FileManagement].isSendOnBehalfEnabled,
  });
};

export const getDetailsBySecureLinkReference = (secureLinkReference: string): Promise<HTTPResponse<any>> => {
  const requestUrl = `${config.api[ServiceType.FileManagement].getSecureLinkDetails}?secureLinkReference=${secureLinkReference}`;
  return http({
    method: httpVerbs.GET,
    url: requestUrl,
  });
};

export const cancelSecureLink = (secureLinkReference: any): Promise<HTTPResponse<any>> => {
  const requestUrl = `${config.api[ServiceType.FileManagement].cancelSecureLink}/${secureLinkReference}/deactivate`;
  return http({
    method: httpVerbs.POST,
    url: requestUrl,
  });
}

export const manualFileSearch = (data: any) => {
  return http({
    method: httpVerbs.POST,
    url: config.api[ServiceType.FileManagement].manualSearch,
    data,
  });
};

export const manualFileSearchAsk = (data: any) => {
  return http({
    method: httpVerbs.POST,
    url: config.api[ServiceType.FileManagement].manualSearchAsk,
    data,
  });
};

export const ocrFileSearch = (data: any) => {
  return http({
    method: httpVerbs.POST,
    url: config.api[ServiceType.FileManagement].ocrSearch,
    data,
  });
};

export const searchCSVDownload = (data: any) => {
  return http({
    method: httpVerbs.POST,
    url: config.api[ServiceType.FileManagement].searchCSV,
    data,
  });
};

export const getFoldersByTemplateId = (templateId: string) => {
  return http({
    method: httpVerbs.GET,
    url: getParameterizedUrlWith(config.api[ServiceType.FileManagement].foldersByTemplateId, [
      {
        name: 'templateId',
        value: templateId,
      },
    ]),
  });
};

export const getPortalFoldersByClientId = (clientId: string): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.GET,
    url: getParameterizedUrlWith(config.api[ServiceType.PortalAdmin].portalFoldersByClientId, [
      {
        name: 'clientId',
        value: clientId,
      },
    ]),
  });
};

export const getDefaultTaxYear = () => {
  return http({
    method: httpVerbs.GET,
    url: config.api[ServiceType.DocumentAdministration].getDefaultTaxYear,
  });
};

export const retentionSearchAsk = (data: any) => {
  return http({
    method: httpVerbs.POST,
    url: config.api[ServiceType.FileManagement].retentionSearchAsk,
    data,
  });
};

export const retentionSearchByCondtions = (data: any) => {
  return http({
    method: httpVerbs.POST,
    url: config.api[ServiceType.FileManagement].retentionSearchCondtions,
    data,
  });
};

export const downloadFileVersion = (fileId: string, versionNumber: string): Promise<HTTPResponse<any>> => {
  return http({
    method: httpVerbs.GET,
    url: getParameterizedUrlWith(
      config.api[ServiceType.FileManagement].downloadFileVersion,
      [{ name: 'fileId', value: fileId }, { name: 'versionNumber', value: versionNumber }]
    ),
  }).then((response) => {
    return http({
      method: httpVerbs.GET,
      url: ServiceType.FileStorage.toString() + response.data.uri,
      responseType: 'arraybuffer',
    });
  });
};

export const createNewFileVersion = (fileId: string, data: any) => {
  return http({
    method: httpVerbs.POST,
    url: getParameterizedUrlWith(config.api[ServiceType.FileManagement].createNewFileVersion, [
      {
        name: 'fileId',
        value: fileId,
      },
    ]),
    data,
  });
};

export const restoreFileVersion = (fileId: string, versionNumber: string, comment: string) => {
  return http({
    method: httpVerbs.POST,
    url: getParameterizedUrlWith(config.api[ServiceType.FileManagement].restoreFileVersion, [
      { name: 'fileId', value: fileId },
      { name: 'versionNumber', value: versionNumber },  
    ]),
    data: { comment },
  });
};

export const getFileConfiguration = () => {
  return http({
    method: httpVerbs.GET,
    url: config.api[ServiceType.DocumentAdministration].fileConfiguration,
  });
};


